<template>
  <div class="cg-chat-card">
    <AiChatCard
      v-if="companyId !== null"
      ref="chat"
      :companyId
      :endpoint="`/api/company/${companyId}/consumption/readings-chat`"
      :greeting="view.askPrompt"
      :extraFields="{ context: contextJson }"
      pendingHint="Reading a document usually takes 10 to 30 seconds."
      @analysis-complete="onAnalysis"
    />
    <div v-else class="card">
      <div class="card-body text-body-secondary small">
        The assistant needs a company on this account to book its uploads
        against.
      </div>
    </div>
  </div>

  <div v-if="proposals.length" class="cg-rule-top mt-4">
    <h3 class="cg-card-title mb-3">Proposed replacements</h3>
    <div class="table-responsive">
      <table class="table table-sm mb-0 align-middle small">
        <thead>
          <tr>
            <th class="cg-micro">Consumption location</th>
            <th class="cg-micro">Period</th>
            <th class="cg-micro text-end">Standing estimate</th>
            <th class="cg-micro text-end">Proposed actual</th>
            <th class="cg-micro text-end">Change</th>
            <th class="cg-micro">Read from</th>
          </tr>
        </thead>
        <tbody class="cg-tabular">
          <tr v-for="row in proposals" :key="row.key">
            <td>
              <span class="fw-medium">{{ row.locationName }}</span>
              <ToneBadge
                v-if="row.meterId === null"
                tone="warn"
                label="No matching location"
                class="ms-2"
              />
            </td>
            <td>{{ row.period }}</td>
            <td class="text-end text-body-secondary">
              {{ row.meterId === null ? "—" : fmt(row.estimate) }}
            </td>
            <td class="text-end fw-semibold">
              {{ fmt(row.proposed) }}
              <ToneBadge
                v-if="row.unitInferred"
                tone="pending"
                label="unit inferred"
                class="ms-1"
              />
            </td>
            <td class="text-end text-body-secondary">
              {{
                row.meterId === null
                  ? "—"
                  : describeDelta(row.proposed - row.estimate)
              }}
            </td>
            <td class="text-body-secondary">
              {{ row.sourceNote ?? basisLabel(row.basis) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="skipped || unmatched" class="text-body-secondary small mt-2 mb-0">
      <template v-if="unmatched"
        >{{ unmatched }} {{ unmatched === 1 ? "row names" : "rows name" }} a
        location that is not in {{ domain.name }}; tell the assistant which
        location it is or add the location first.
      </template>
      <template v-if="skipped"
        >{{ skipped }} {{ skipped === 1 ? "row" : "rows" }} without a usable
        month or volume left out.</template
      >
    </p>
    <div class="d-flex gap-2 mt-3 flex-wrap align-items-center">
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="summary.count === 0"
        @click="reviewOpen = true"
      >
        Review and confirm {{ summary.count }}
        {{ summary.count === 1 ? "replacement" : "replacements" }}
      </button>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        @click="discard"
      >
        Discard
      </button>
    </div>
  </div>
  <div v-else-if="confirmedCount" class="alert alert-success small mb-4">
    <strong>Confirmed.</strong> {{ confirmedCount }} location-month{{
      confirmedCount === 1 ? "" : "s"
    }}
    in {{ domain.name }} now read as actual. The grid, the totals and the claim
    position have moved with them.
  </div>

  <!-- Hand-entered figures stay one click away so the chat keeps the focus when the modal opens. -->
  <div class="cg-rule-top mt-4">
    <button
      type="button"
      class="btn btn-link p-0 d-flex align-items-center gap-2 text-decoration-none text-body"
      :aria-expanded="entriesOpen"
      @click="entriesOpen = !entriesOpen"
    >
      <font-awesome-icon
        :icon="entriesOpen ? 'chevron-down' : 'chevron-right'"
        class="text-body-secondary"
      />
      <h3 class="cg-card-title mb-0">Reported actuals</h3>
      <span class="badge bg-body-secondary text-body-secondary border">{{
        entries.length
      }}</span>
    </button>
    <div v-if="entriesOpen" class="border rounded overflow-hidden mt-3">
      <div class="cg-entry-row cg-entry-head bg-body-secondary border-bottom">
        <span class="cg-micro">Consumption location</span
        ><span class="cg-micro">Period</span
        ><span class="cg-micro text-end">Actual · MWh</span><span />
      </div>
      <div
        v-for="(entry, index) in entries"
        :key="index"
        class="cg-entry-row border-bottom"
      >
        <select
          :value="entry.name"
          class="form-select form-select-sm border-0"
          aria-label="Consumption location"
          @change="
            update(index, 'name', ($event.target as HTMLSelectElement).value)
          "
        >
          <option value="">Choose a consumption location</option>
          <option v-for="name in view.meterOptions" :key="name" :value="name">
            {{ name }}
          </option>
        </select>
        <select
          :value="entry.period"
          class="form-select form-select-sm border-0"
          aria-label="Period"
          @change="
            update(index, 'period', ($event.target as HTMLSelectElement).value)
          "
        >
          <option value="">Choose a period</option>
          <option v-for="p in view.periodOptions" :key="p" :value="p">
            {{ p }}
          </option>
        </select>
        <input
          :value="entry.value"
          type="text"
          inputmode="numeric"
          class="form-control form-control-sm border-0 text-end cg-tabular"
          aria-label="Actual MWh"
          @change="
            update(index, 'value', ($event.target as HTMLInputElement).value)
          "
        />
        <button
          type="button"
          class="btn btn-sm btn-link text-body-secondary p-1"
          aria-label="Remove row"
          @click="remove(index)"
        >
          <font-awesome-icon icon="xmark" />
        </button>
      </div>
      <button
        type="button"
        class="btn btn-sm btn-link text-start w-100 py-2 px-3"
        @click="add"
      >
        <font-awesome-icon icon="plus" class="me-2" />Add a reported figure
      </button>
    </div>
    <div
      v-if="entriesOpen"
      class="d-flex justify-content-between align-items-center gap-2 mt-3 flex-wrap"
    >
      <span class="text-body-secondary small"
        >{{ entries.length }} rows · unsaved changes are kept locally</span
      >
      <button type="button" class="btn btn-sm btn-primary">Save entries</button>
    </div>
  </div>

  <ConfirmReplacementsModal
    v-if="reviewOpen"
    :summary
    @confirm="confirm"
    @close="reviewOpen = false"
  />
</template>

<script setup lang="ts">
import type { ConsumptionEntry } from "@/compliance_groups/fixture/complianceGroupsTypes";
import type {
  ConsumptionView,
  DerivedDomain,
} from "@/compliance_groups/fixture/derive";
import { fmt } from "@/compliance_groups/fixture/format";
import {
  buildReadingsContext,
  describeDelta,
  mapReadingsToProposals,
  summarizeProposals,
  toConfirmedReadings,
  type ChatReading,
  type ReadingProposal,
  type ReadingsChatPayload,
} from "@/compliance_groups/readingsChat";
import { useComplianceDemoStore } from "@/compliance_groups/useComplianceDemoStore";
import { useAccountStore } from "@/composables/account/useAccountStore";
import AiChatCard from "@/dashboard/inventory_management/common/AiChatCard.vue";
import { computed, ref, useTemplateRef } from "vue";
import ConfirmReplacementsModal from "./ConfirmReplacementsModal.vue";
import ToneBadge from "./ToneBadge.vue";

const props = defineProps<{ domain: DerivedDomain; view: ConsumptionView }>();
// Exposed so a host modal can hold off its own dismissal while the confirm dialog sits on top.
const reviewOpen = defineModel<boolean>("reviewOpen", { default: false });

const store = useComplianceDemoStore();
const accountStore = useAccountStore();
// The chat books uploads and quota against one of the account's companies; the readings
// themselves only ever land in the demo store. ComplianceGroupsLayout renders this page only once
// the account has loaded.
const companyId = computed(() => accountStore.account.companies[0]?.id ?? null);
const contextJson = computed(() =>
  JSON.stringify(buildReadingsContext(props.domain))
);

const entriesOpen = ref(false);
const proposals = ref<ReadingProposal[]>([]);
const skipped = ref(0);
const confirmedCount = ref(0);
const unmatched = computed(
  () => proposals.value.filter((p) => p.meterId === null).length
);
const summary = computed(() =>
  summarizeProposals(props.domain, proposals.value)
);

// Every chat turn returns the whole draft, so the table always mirrors the latest draft.
const onAnalysis = (payload: unknown) => {
  if (!payload) {
    proposals.value = [];
    skipped.value = 0;
    confirmedCount.value = 0;
    return;
  }
  const mapped = mapReadingsToProposals(
    props.domain,
    payload as ReadingsChatPayload
  );
  proposals.value = mapped.proposals;
  skipped.value = mapped.skipped;
  confirmedCount.value = 0;
};
// Confirming abandons the conversation too: its draft still holds the rows just placed, and a
// later turn would hand them back for a second confirmation over newer readings. The reset
// clears the banner, so the count is set afterwards.
const confirm = () => {
  const rows = toConfirmedReadings(proposals.value);
  store.applyReadings(props.domain.key, rows);
  proposals.value = [];
  skipped.value = 0;
  chat.value?.resetChat();
  confirmedCount.value = rows.length;
};
const chat = useTemplateRef<InstanceType<typeof AiChatCard>>("chat");
// Discarding abandons the conversation too: its draft is persisted server-side, and the next turn
// would otherwise merge the discarded rows straight back in.
const discard = () => {
  proposals.value = [];
  skipped.value = 0;
  confirmedCount.value = 0;
  chat.value?.resetChat();
};
const basisLabel = (basis: ChatReading["basis"]): string =>
  basis === "meter_reading"
    ? "Meter reading"
    : basis === "invoice"
      ? "Invoice"
      : basis === "estimate"
        ? "Supplier estimate"
        : "Document";

const entries = computed<ConsumptionEntry[]>(() => [
  ...(store.state.consumptionEntries[props.domain.key] ??
    props.view.baseEntries),
]);
const commit = (next: ConsumptionEntry[]) =>
  store.setConsumptionEntries(props.domain.key, next);
const update = (index: number, field: keyof ConsumptionEntry, value: string) =>
  commit(
    entries.value.map((e, i) => (i === index ? { ...e, [field]: value } : e))
  );
const remove = (index: number) =>
  commit(entries.value.filter((_, i) => i !== index));
const add = () =>
  commit([...entries.value, { name: "", period: "", value: "" }]);
</script>

<style scoped lang="scss">
// Hairlines here match the feature's cards (the generic border utilities are a darker grey).
.border,
.border-bottom {
  border-color: var(--phoenix-card-border-color) !important;
}
// The shared chat card stretches to its parent's height; give it a sensible floor here.
.cg-chat-card :deep(.chat-thread) {
  min-height: 150px;
  max-height: 360px;
  overflow-y: auto;
}
.cg-entry-row {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr) 132px 34px;
  gap: 12px;
  align-items: center;
  padding: 3px 12px;
}
.cg-entry-head {
  padding: 8px 12px;
}
</style>
